import { apiService } from 'apis/site_recycle/siteApi';
import { CompressionBookingResponse } from 'types/compression';
import { addition } from 'util/Calc';
import {
  Compression,
  ProcessedProductHistoryParams,
  ProcessedProductHistoryResponse,
  RawMaterialHistoryParams,
  RawMaterialHistoryResponse,
  PreRegisResource,
  ProcessingMaterialsParams,
  ProcessingMaterialsResponse,
  UserCompressionCreateRequest,
  UserCompressionInfo,
  StorageSiteHistoryParams,
  StorageSiteHistoryResponse,
} from 'util/Types';
import { API_ENDPOINTS } from 'util/endPoints';

const addTagTypes = ['userCompression'] as const;

const compressionUserApi = apiService
  .enhanceEndpoints({ addTagTypes })
  .injectEndpoints({
    endpoints: (build) => ({
      fetchAllCompressions: build.query<Compression[], void>({
        query: () => ({
          url: '/compressions',
          method: 'GET',
        }),
        providesTags: (result, error) => {
          if (error) return [];
          return ['userCompression'];
        },
      }),
      fetchCompressionInfo: build.query<
        { receiveTotal: number; registTotal: number },
        void
      >({
        query: () => ({
          url: '/compressions/info',
          method: 'GET',
        }),
        providesTags: ['userCompression'],
        transformResponse(baseQueryReturnValue: UserCompressionInfo) {
          const receiveWeight =
            baseQueryReturnValue.receives.reduce(
              (prev, receive) => addition(prev, parseFloat(receive.weight)),
              0
            ) || 0;
          const registWeight =
            baseQueryReturnValue.compressions.reduce(
              (prev, compression) =>
                addition(prev, parseFloat(compression.weight)),
              0
            ) || 0;
          return {
            receiveTotal: receiveWeight,
            registTotal: registWeight,
          };
        },
      }),
      createCompression: build.mutation<
        void,
        {
          data: Partial<UserCompressionCreateRequest>;
          isAuto?: boolean;
          isConsecutive?: boolean;
        }
      >({
        query: ({ data, isAuto, isConsecutive }) => {
          if (isAuto) {
            data.inputResourceIds =
              data.inputResources?.map((r) => r.processingObjectId) || [];

            delete data.inputResources;
          }

          if (!isConsecutive) {
            delete data.prevProcessingObjectId;
          }

          if (isAuto && isConsecutive) {
            data.isContinueProcessing = true;
          }

          return {
            url: isAuto ? '/compressions/auto-processing' : '/compressions',
            method: 'POST',
            data,
          };
        },
        invalidatesTags: (result, error) => (error ? [] : ['userCompression']),
      }),
      fetchSiteInfoById: build.query({
        query: (siteId: string | null) => ({
          url: `sites/${siteId}/info`,
          method: 'GET',
        }),
      }),
      updateCompression: build.mutation({
        query: (data) => ({
          url: '/compressions',
          method: 'PUT',
          data: {
            compression: data,
          },
        }),
        invalidatesTags: (result, error) => (error ? [] : ['userCompression']),
      }),
      createCompressionDeliveries: build.mutation({
        query: (data) => ({
          url: '/compressions/booking-deliveries',
          method: 'POST',
          data: data,
        }),
        invalidatesTags: (result, error) => (error ? [] : ['userCompression']),
      }),
      fetchCompressionBookings: build.query<CompressionBookingResponse, void>({
        query: () => ({
          url: '/compressions/bookings',
          method: 'GET',
        }),
        providesTags: ['userCompression'],
      }),
      fetchNextStageSites: build.mutation({
        query: (data) => ({
          url: '/compressions/next-stage-sites',
          method: 'POST',
          data: data,
        }),
        transformResponse(
          baseQueryReturnValue: { id: string; name: string }[]
        ) {
          return baseQueryReturnValue.map((x) => ({
            label: x.name,
            value: x.id,
          }));
        },
        invalidatesTags: (result, error) => (error ? [] : ['userCompression']),
      }),
      fetchCompressionResourceInput: build.query<PreRegisResource[], void>({
        query: () => ({
          url: API_ENDPOINTS.COMPRESSIONS_INPUT_RESOURCE,
          method: 'GET',
        }),
      }),
      fetchProcessingMaterials: build.query<
        ProcessingMaterialsResponse,
        ProcessingMaterialsParams
      >({
        query: ({ page, take, materialName }) => ({
          url: '/compressions/processing-materials',
          method: 'GET',
          params: {
            page,
            take,
            ...(materialName ? { materialName } : {}),
          },
        }),
      }),
      resetInputResources: build.mutation<
        void,
        { processingObjectIds: string[] }
      >({
        query: (data) => ({
          url: API_ENDPOINTS.COMPRESSIONS_INPUT_RESOURCE_RESET,
          method: 'POST',
          data,
        }),
      }),
      fetchRawMaterialHistory: build.query<
        RawMaterialHistoryResponse,
        RawMaterialHistoryParams
      >({
        query: ({ dateFrom, dateTo, page, take }) => ({
          url: API_ENDPOINTS.WORK_HISTORIES_COMPRESSION_RESOURCE_RECEIVED,
          method: 'GET',
          params: {
            ...(dateFrom ? { dateFrom } : {}),
            ...(dateTo ? { dateTo } : {}),
            page,
            take,
          },
        }),
      }),
      fetchProcessedProductHistory: build.query<
        ProcessedProductHistoryResponse,
        ProcessedProductHistoryParams
      >({
        query: ({ dateFrom, dateTo, page, take }) => ({
          url: API_ENDPOINTS.WORK_HISTORIES_COMPRESSION_PROCESSED_PRODUCTS,
          method: 'GET',
          params: {
            ...(dateFrom ? { dateFrom } : {}),
            ...(dateTo ? { dateTo } : {}),
            page,
            take,
          },
        }),
      }),
      updateSubSiteResource: build.mutation<
        void,
        { id: string; weight: string }
      >({
        query: ({ id, weight }) => ({
          url: `${API_ENDPOINTS.SUB_SITE_RESOURCES}/${id}`,
          method: 'PUT',
          data: { weight },
        }),
      }),
      fetchStorageSiteHistory: build.query<
        StorageSiteHistoryResponse,
        StorageSiteHistoryParams
      >({
        query: ({ dateFrom, dateTo, page, take }) => ({
          url: API_ENDPOINTS.WORK_HISTORIES_STORAGE_SITE_HISTORY,
          method: 'GET',
          params: {
            ...(dateFrom ? { dateFrom } : {}),
            ...(dateTo ? { dateTo } : {}),
            page,
            take,
          },
        }),
      }),
    }),
  });

export const {
  useFetchAllCompressionsQuery,
  useFetchCompressionInfoQuery,
  useCreateCompressionMutation,
  useFetchSiteInfoByIdQuery,
  useUpdateCompressionMutation,
  useCreateCompressionDeliveriesMutation,
  useFetchCompressionBookingsQuery,
  useFetchNextStageSitesMutation,
  useFetchCompressionResourceInputQuery,
  useFetchProcessingMaterialsQuery,
  useResetInputResourcesMutation,
  useFetchRawMaterialHistoryQuery,
  useFetchProcessedProductHistoryQuery,
  useUpdateSubSiteResourceMutation,
  useFetchStorageSiteHistoryQuery,
} = compressionUserApi;
